import { useParams } from 'react-router';

import { aliasLegacyTenantSlug } from '@/router/tenant-path';
import { findTenantBySlug } from '@/router/tenant-routing';

import { useAuth } from './use-auth';

/**
 * Returns the active tenant slug from the `:tenantSlug` URL segment.
 *
 * Returns `null` on global routes (e.g. `/login*`) with no tenant segment.
 */
export function useTenantSlug(): string | null {
  const params = useParams<{ tenantSlug?: string }>();
  return params.tenantSlug ? aliasLegacyTenantSlug(params.tenantSlug) : null;
}

/**
 * Returns the active SaaS tenant id for the current view.
 *
 * Resolution order:
 *   1. The `:tenantSlug` URL segment → canonical id via `user.saas_tenants[]`
 *   2. The session tenant from `/api/me` (`user.saas_tenant_id`) — covers global
 *      routes such as `/login*` where the URL has no tenant segment, plus the
 *      brief window before the gate aligns the URL and the session.
 *
 * Returns `null` when no tenant can be determined (unauthenticated boot).
 */
export function useTenantId(): string | null {
  const tenantSlug = useTenantSlug();
  const { user } = useAuth();

  if (tenantSlug && user?.saas_tenants) {
    return findTenantBySlug(tenantSlug, user.saas_tenants)?.id ?? null;
  }

  return user?.saas_tenant_id ?? null;
}
